import { lookbackDrift } from './evaluate'
import { SETTING_FIELDS, needsCarc } from './types'
import type {
  PolicyDocView,
  PolicyDataset,
  PolicyInsight,
  PolicyMetadata,
  PredicateData,
} from './types'

export interface TreeInput {
  nodes: PredicateData[]
  insights: PolicyInsight[]
  metadata: PolicyMetadata[]
  datasets: PolicyDataset[]
}

/**
 * Structural errors in a decision tree, in the builder's own wording.
 *
 * Ported from the engine designer store's `validateTree` so the stage gate and
 * the editor agree; the only change is that it reads plain arrays instead of
 * store state and returns the errors instead of writing them to a ref. Paths
 * name their target by label — an insight's label or a predicate's label —
 * exactly as the builder resolves them.
 */
export const validateTree = ({
  nodes,
  insights,
  metadata,
  datasets,
}: TreeInput): string[] => {
  const errors: string[] = []

  const ids = new Set([
    ...nodes.map((node) => node.id),
    ...insights.map((insight) => insight.label),
  ])
  if (ids.size !== nodes.length + insights.length)
    errors.push(
      'ID Collision detected. Please ensure that insight IDs and predicate labels are unique.',
    )

  errors.push(
    ...metadata.flatMap(({ text }, i) =>
      text?.trim() ? [] : [`Metadata ${i + 1} is empty.`],
    ),
    ...datasets.flatMap(({ name, url }, i) => [
      ...(name?.trim() ? [] : [`Dataset ${i + 1} is missing a name.`]),
      ...(url?.trim() ? [] : [`Dataset ${i + 1} is missing a URL.`]),
    ]),
    ...insights
      .filter(
        (insight) =>
          needsCarc(insight.insightType) && !insight.reasonCodeLevel1?.trim(),
      )
      .map((insight) => `Outcome "${insight.label}" needs a CARC.`),
  )

  const targetOf = (label: string): string | undefined =>
    insights.find((insight) => insight.label === label)?.id ??
    nodes.find((node) => node.label === label)?.id

  const inEdges = new Set<string>()
  const outEdges = new Set<string>()
  for (const node of nodes)
    for (const path of [node.yesPath, node.noPath]) {
      const target = targetOf((path || '').trim())
      if (!path?.trim() || !target) continue
      outEdges.add(node.id)
      inEdges.add(target)
    }

  for (const node of nodes) {
    if (node.yesPath && !targetOf(node.yesPath))
      errors.push(
        `Node "${node.label}" references non-existent yes path "${node.yesPath}".`,
      )
    if (!node.yesPath)
      errors.push(`Decision node "${node.label}" is missing its YES path.`)
    if (node.noPath && !targetOf(node.noPath))
      errors.push(
        `Node "${node.label}" references non-existent no path "${node.noPath}".`,
      )
    if (!node.noPath)
      errors.push(`Decision node "${node.label}" is missing its NO path.`)
  }

  const all = [
    ...nodes.map((node) => ({
      id: node.id,
      label: node.label,
      type: 'predicate',
    })),
    ...insights.map((insight) => ({
      id: insight.id,
      label: insight.label,
      type: 'insight',
    })),
  ]
  const roots = all.filter(
    (node) => !inEdges.has(node.id) && outEdges.has(node.id),
  )
  const orphans = all.filter(
    (node) => !inEdges.has(node.id) && !outEdges.has(node.id),
  )

  if (!roots.length)
    errors.push(
      'No root node found. There should be at least one node not referenced by any other node.',
    )
  if (roots.length > 1)
    errors.push(
      'Multiple root nodes found. There should only be one root node.',
    )
  for (const node of orphans)
    errors.push(`Orphaned ${node.type} node ${node.label} should be connected.`)

  /* Paths name targets by label, while predicates are stored by id. Resolve a
     path through the same target lookup as the structural checks before
     walking it, so a label-addressed cycle cannot be skipped. */
  const predicates = new Map(nodes.map((node) => [node.id, node]))
  const visited = new Set<string>()
  const stack = new Set<string>()
  const hasCycle = (target: string): boolean => {
    if (!target) return false
    const id = targetOf(target) ?? target
    if (stack.has(id)) return true
    if (visited.has(id)) return false
    visited.add(id)
    stack.add(id)
    const node = predicates.get(id)
    if (
      node &&
      ((node.yesPath && hasCycle(node.yesPath)) ||
        (node.noPath && hasCycle(node.noPath)))
    )
      return true
    stack.delete(id)
    return false
  }
  const cyclic = all.find((node) => !visited.has(node.id) && hasCycle(node.id))
  if (cyclic)
    errors.push(
      `Cycle detected in the tree starting from node "${cyclic.label}".`,
    )

  return errors
}

/** What design should look at but may approve past: today, a Lookback
 *  setting that differs from scoping's answer (D-N2). */
export const treeWarnings = (
  view: Pick<PolicyDocView, 'answers' | 'nodes'>,
): string[] =>
  lookbackDrift(view).map(({ nodeLabel, settingId, scoping }) => {
    const label = SETTING_FIELDS.decision.find((f) => f.id === settingId)!.label
    return `Node "${nodeLabel}" ${label.toLowerCase()} differs from scoping (${scoping}).`
  })
